import { Button } from '@bambi/component-library/src/components/Button/Button';
import { Combobox } from '@bambi/component-library/src/components/inputs/Combobox/Combobox';
import { Select } from '@bambi/component-library/src/components/inputs/Select/Select';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useChangeOrganization } from './hooks/useChangeOrganization';
import { Modal } from './primitives/Modal';
import { show } from './primitives/Toast/toast.slice';
import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import { authSlice } from 'src/features/auth/auth.slice';

export function ChangeOrganizationModal() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.profile);
  const currentOrganizationId = useAppSelector(
    (state) => state.auth.currentOrganizationId
  );
  const isOrganizationChangeOpen = useAppSelector(
    (state) => state.auth.isOrganizationChangeOpen
  );

  const [newOrganizationSelection, setNewOrganizationSelection] = useState(
    currentOrganizationId
  );
  const [organizationOptions, setOrganizationOptions] = useState<
    SelectOption[]
  >([]);

  useEffect(() => {
    setOrganizationOptions(
      user?.memberships.map((membership) => {
        return {
          label: membership.organization.name,
          value: membership.organization.id || '',
        };
      }) || []
    );
  }, [user]);

  useEffect(() => {
    setNewOrganizationSelection(currentOrganizationId);
  }, [currentOrganizationId]);

  const selectedOrganization = useMemo(() => {
    return organizationOptions.find(
      (opt) => opt.value === newOrganizationSelection
    );
  }, [organizationOptions, newOrganizationSelection]);

  const changeOrganization = useChangeOrganization();
  const handleOrganizationChange = useCallback(() => {
    if (!newOrganizationSelection) {
      dispatch(
        show({
          type: 'error',
          title: 'No organization selected',
        })
      );
      return;
    }

    changeOrganization(newOrganizationSelection);
  }, [changeOrganization, dispatch, newOrganizationSelection]);

  return (
    <Modal
      open={isOrganizationChangeOpen}
      setOpen={() => {
        dispatch(authSlice.actions.setIsOrganizationChangeOpen(false));
      }}
      title="Which organization would you like to use?"
    >
      <div className="flex flex-col gap-2">
        <h2 className="text-md font-bold">
          Which organization would you like to use?
        </h2>
        {organizationOptions.length > 5 ? (
          <Combobox
            options={organizationOptions}
            value={selectedOrganization}
            onChange={(newValue) => {
              setNewOrganizationSelection(newValue.value);
            }}
            placeholder="Select an organization"
            dataTestId="organization-combobox"
          />
        ) : (
          <Select
            options={organizationOptions}
            value={selectedOrganization?.value || ''}
            onChange={(newValue) => {
              setNewOrganizationSelection(newValue?.value || '');
            }}
          />
        )}
        <Button variant="primary" onClick={handleOrganizationChange}>
          Change Current Organization
        </Button>
      </div>
    </Modal>
  );
}
